// #region Global Imports
import * as React from 'react';
// #endregion Global Imports
// #region Local Imports
import get from 'lodash/get';
import { useQuery } from '@apollo/client';
import {
  addApolloState,
  initializeApollo,
  withDefaultVariables,
} from '@services/apollo';
import {
  PageContent,
  PageContentFragment,
} from '@components/PageContent/PageContent';
import { WrapperContent } from '@components/Wrapper';
import type { IPages } from 'types/pages';
import { PageQueryDocument } from '@generated/graphql';
import type { FragmentType } from '@generated/fragment-masking';
import { getFragmentData } from '@generated/fragment-masking';
import { Layout } from '@components/Layout';
import { withSession } from '@lib/auth';
import type { WebformQuery } from '@components/Webform';
import Webform from '@components/Webform';
import { Spinner } from '@components/Spinner';
// #endregion Local Imports

export const Page = ({
  variables,
  language,
  user,
}: IPages.Page): React.ReactElement => {
  const { data, loading } = useQuery(PageQueryDocument, {
    variables,
  });

  if (loading) return <Spinner />;

  const page = getFragmentData(
    PageContentFragment,
    get(data, ['route', 'page']),
  );

  const webform = get(page, ['webform']);

  return (
    <Layout
      data={data}
      language={language}
      title={page.title ?? ''}
      user={user ?? {}}
    >
      <WrapperContent>
        <PageContent
          pageContent={page as FragmentType<typeof PageContentFragment>}
        />
        {webform ? (
          <Webform
            lang={language.languageId}
            webform={webform as FragmentType<typeof WebformQuery>}
          />
        ) : null}
      </WrapperContent>
    </Layout>
  );
};

export const getServerSideProps = withSession(async (context) => {
  const apolloClient = initializeApollo(null, context.req.jwt);
  const variables = withDefaultVariables(context, {});

  const { data, error } = await apolloClient.query({
    query: PageQueryDocument,
    variables,
  });

  if (!data.route || error) {
    return {
      notFound: true,
    };
  }

  return addApolloState(context, apolloClient, {
    props: {
      variables,
    },
  });
});

export default Page;
